// Global state shared across element instances (survives DOM replacement by refreshCart)
const RT = window.__rewardThresholds = window.__rewardThresholds || {
  busy: false,
  giftInCart: false,
  giftLineKey: null,
  // True when the customer explicitly declined the auto-added free gift
  // ("No thanks"). Source of truth is the cart attribute `_gift_declined`
  // (survives reload, visible in /cart.js as cart.attributes); this in-memory
  // mirror is set synchronously on click so a mid-flight processCart can't
  // re-add the gift before the attribute write lands. Cleared when the
  // customer re-adds the gift or drops below the gift threshold.
  giftDeclined: false,
  shippingEarned: false,
  giftEarned: false,
  debounceTimer: null,
  fetchController: null,
  initialized: false,
  config: null,
  shippingEligible: true,
  lastItemCount: 0,
  // Set when /cart/add.js rejects the gift as unavailable (inventory 0 on a
  // DENY variant returns 422). Without this the pill kept claiming the gift
  // was in the cart — updateUI() keys the earned state on the threshold
  // comparison, not on giftInCart — and the celebration banner fired over a
  // cart with no gift in it. Cleared when the cart drops below the threshold
  // so the next qualifying cart tries again.
  giftUnavailable: false,
  // Cart signature (non-gift lines) at the moment the gift was refused, and
  // the live one. Used to retry once the customer changes the cart.
  giftUnavailableAtKey: null,
  cartKey: null,
};

// Checkout interceptor (codex #3 + #7, redesigned after second review):
// one document-level gate that (a) flushes a pending cart note and (b) waits
// out the gift-reconcile busy lock before letting checkout proceed. Fixes
// from the review of v1: re-query the live button at release time
// (refreshCart replaces the cart DOM, a captured ref goes detached); a
// `releasing` bypass so the synthetic click passes the gate even if RT.busy
// is still true at the 5s safety valve (v1 re-intercepted its own click
// forever); a `pending` latch so rapid clicks can't stack intervals or
// double-submit. Also replaces the per-<cart-note> document listener
// (leaked one listener per cart re-render).
if (!window.__rtCheckoutGate) {
  window.__rtCheckoutGate = true;
  let releasing = false;
  let pending = false;
  document.addEventListener('click', (e) => {
    const btn = e.target && e.target.closest && e.target.closest('[name="checkout"]');
    if (!btn) return;
    if (releasing) { releasing = false; return; }
    const note = document.querySelector('cart-note');
    const needsFlush = !!(note && typeof note.flush === 'function');
    if (!RT.busy && !needsFlush) return;
    e.preventDefault();
    e.stopPropagation();
    if (pending) return;
    pending = true;
    btn.setAttribute('aria-busy', 'true');
    const started = Date.now();
    const release = () => {
      pending = false;
      // Re-query: refreshCart may have replaced the button since the click.
      const live = document.querySelector('[name="checkout"]') || btn;
      live.removeAttribute('aria-busy');
      releasing = true;
      live.click();
    };
    // Race the note flush against a 1.2s cap (codex): a flush whose network
    // request never settles must not stall checkout — the 5s busy valve only
    // starts after this resolves. keepalive still carries the POST through the
    // navigation even if we stop waiting on it here.
    const flushCall = needsFlush ? Promise.resolve(note.flush()).catch(() => {}) : Promise.resolve();
    const flushed = Promise.race([flushCall, new Promise((r) => setTimeout(r, 1200))]);
    flushed.then(() => {
      if (!RT.busy) { release(); return; }
      const wait = setInterval(() => {
        if (!RT.busy || Date.now() - started > 5000) {
          clearInterval(wait);
          release();
        }
      }, 120);
    });
  }, true);
}

// Centralized money formatting — uses Shopify's shop.money_format via Combine's KROWN settings
function rtMoneyFmt() {
  return (typeof KROWN !== 'undefined' && KROWN.settings && KROWN.settings.shop_money_format)
    ? KROWN.settings.shop_money_format : '${{amount}}';
}
function rtFormatMoney(cents) {
  const amount = (cents / 100).toFixed(2);
  return rtMoneyFmt().replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.round(cents / 100));
}
function rtFormatWhole(dollars) {
  return rtMoneyFmt().replace('{{amount}}', dollars + '.00').replace('{{amount_no_decimals}}', dollars);
}
// Whole-dollar format, NO ".00" decimals. Used everywhere the progress
// copy says "$X" — nobody needs to see "$39.00 to go" when "$39 to go"
// is cleaner and takes less horizontal space on mobile.
function rtFormatWholeNoCents(dollars) {
  const fmt = rtMoneyFmt();
  // Prefer shop's {{amount_no_decimals}} format token if the money
  // format string uses it. Otherwise replace {{amount}} with the
  // bare dollar integer (no trailing .00).
  if (fmt.indexOf('{{amount_no_decimals}}') !== -1) {
    return fmt.replace('{{amount_no_decimals}}', dollars);
  }
  return fmt.replace('{{amount}}', String(dollars));
}
// Warmer, tiered progress copy. Trek Light voice: casual-warm, never salesy.
//   > $25 away → "Just $X away" (calm)
//   <= $25    → "So close — $X to go" (encouraging)
//   <= $10    → "Almost there! Just $X" (urgency)
// Change copy here, not at the call sites, so every pill + primed state
// + status line stay consistent.
// A tier is "active" only when it can actually be earned. Both pills are
// hidden and excluded from every earned/celebration calculation when their
// tier is off, so switching the gift tier off in the theme editor leaves a
// clean free-shipping-only cart with no stale copy or half-width layout.
function rtGiftActive() {
  return !!(RT.config && RT.config.giftEnabled && RT.config.giftThreshold > 0 && RT.config.giftVariantId);
}

function rtShippingActive() {
  return !!(RT.config && RT.shippingEligible && RT.config.shippingThreshold > 0);
}

// Celebration copy must name only the tiers that are actually running:
// combined when both are live, shipping-only when the gift tier is off, and
// gift-only for a geo-ineligible visitor who still qualifies for the gift.
// (Codex P1: the combined default promised free shipping to customers who
// cannot get it.)
function rtCelebrationText() {
  const shipping = rtShippingActive();
  const gift = rtGiftActive();
  if (shipping && gift) return RT.config.celebrationText;
  if (shipping) return RT.config.celebrationTextShippingOnly || RT.config.celebrationText;
  if (gift) return (RT.config.giftText || 'Free gift') + ' unlocked!';
  return '';
}

function rtProgressCopy(remainingDollars) {
  const amt = rtFormatWholeNoCents(remainingDollars);
  if (remainingDollars <= 10) return 'Almost there! Just ' + amt;
  if (remainingDollars <= 25) return 'So close, ' + amt + ' to go';
  return 'Just ' + amt + ' away';
}
// Escape HTML to prevent XSS from product data
function rtEscapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

// Wait for Combine's refreshCart to finish (it replaces innerHTML but fires no event)
function rtWaitForCartRefresh(callback) {
  const target = document.getElementById('AjaxCartSubtotal');
  if (!target) { setTimeout(callback, 500); return; }
  let fired = false;
  const once = () => { if (fired) return; fired = true; observer.disconnect(); clearTimeout(tid); callback(); };
  const observer = new MutationObserver(once);
  observer.observe(target, { childList: true });
  const tid = setTimeout(once, 3000);
}

// Global helper: fetch cart ONCE, then update ALL instances
function rtScheduleRecalculate() {
  clearTimeout(RT.debounceTimer);
  RT.debounceTimer = setTimeout(async () => {
    if (RT.fetchController) RT.fetchController.abort();
    RT.fetchController = new AbortController();
    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        signal: RT.fetchController.signal
      });
      const cart = await response.json();
      document.querySelectorAll('reward-thresholds').forEach(el => {
        if (el.processCart) el.processCart(cart);
      });
    } catch (e) {
      if (e.name !== 'AbortError') console.error('RewardThresholds:', e);
    }
  }, 100);
}

if (!customElements.get('reward-thresholds')) {
  customElements.define('reward-thresholds', class RewardThresholds extends HTMLElement {

    connectedCallback() {
      // Read config from JSON settings block
      const settingsEl = document.getElementById('RewardThresholdsSettings');
      if (!settingsEl) {
        this.remove();
        return;
      }

      let settings;
      try {
        settings = JSON.parse(settingsEl.textContent);
        if (!settings.enabled) {
          this.remove();
          return;
        }
      } catch (e) {
        console.error('RewardThresholds: Failed to parse settings', e);
        this.remove();
        return;
      }

      // Store config globally (only first time, or refresh from settings)
      RT.config = {
        shippingThreshold: settings.shippingThreshold || 0,
        giftThreshold: settings.giftThreshold || 0,
        giftVariantId: settings.giftVariantId || '',
        // Merchant kill switch for the whole gift tier. Absent (older cart
        // template JSON) reads as on, so the toggle can't dark-launch itself.
        giftEnabled: settings.giftEnabled !== false,
        country: (settings.country || '').toUpperCase(),
        shippingCountries: (settings.shippingCountries || 'US').split(',').map(c => c.trim().toUpperCase()),
        enableSticky: settings.enableSticky,
        enableMobileCheckout: settings.enableMobileCheckout !== false,
        enableUpsell: settings.enableUpsell,
        celebrationText: settings.celebrationText || '',
        celebrationTextShippingOnly: settings.celebrationTextShippingOnly || '',
        upsellHeading: settings.upsellHeading || '',
        giftText: settings.giftText || '',
        giftValue: settings.giftValue || '',
        shippingText: settings.shippingText || '',
        shippingSubtitle: settings.shippingSubtitle || '',
        giftImage: settings.giftImage || '',
        showGiftImage: settings.showGiftImage,
        brandMessages: settings.brandMessages || [],
        pwycMessage: settings.pwycMessage || '',
        previewPwyc: settings.previewPwyc,
      };

      // Forward the single most-visible merchant-configurable color
      // (section setting 'color_accent') onto --rt-accent as an inline
      // style. This keeps any merchant customization working — they can
      // still change the progress-bar + earned-icon color via the theme
      // editor. The section schema default has been updated to Variant D
      // terracotta (#d4491f), so uncustomized merchants get the new
      // design by default. Merchants who customized get their color.
      //
      // We deliberately do NOT forward the other --rt-* legacy tokens
      // (pill-bg, pill-border, icon-bg, text-strong, text-muted,
      // celebration-*). The Variant D CSS doesn't use those tokens — the
      // cart drives surface / text / ribbon colors from --cart-*
      // directly — so forwarding them would be dead data. If Seth wants
      // those controls back, remove the corresponding section-schema
      // entries in a coordinated migration.
      if (settings.colorAccent) {
        this.style.setProperty('--rt-accent', settings.colorAccent);
      }

      // Set initial labels.
      const shippingLabel = this.querySelector('[data-rt-label="shipping"]');
      if (shippingLabel) shippingLabel.textContent = RT.config.shippingText;
      const giftLabel = this.querySelector('[data-rt-label="gift"]');
      if (giftLabel) giftLabel.textContent = RT.config.giftText;

      // Prime initial subtitles from server-rendered cart total so pills
      // show progress copy immediately on first paint (prevents a blank
      // flash of empty subtitles on slow networks while /cart.js resolves).
      // Uses the same tiered copy updateUI() will use, so text never shifts.
      const initialCartTotal = (settings.cartTotal != null) ? settings.cartTotal : 0;
      const initialShippingEligible =
        RT.config.shippingCountries.includes(RT.config.country) ||
        RT.config.country === '';
      const primeSub = (pill, threshold, earnedText) => {
        if (!pill || !threshold) return;
        const sub = pill.querySelector('.reward-thresholds__subtitle');
        if (!sub) return;
        if (initialCartTotal >= threshold) {
          sub.textContent = earnedText;
        } else {
          const remaining = Math.ceil((threshold - initialCartTotal) / 100);
          sub.textContent = rtProgressCopy(remaining);
        }
      };
      primeSub(
        this.querySelector('[data-rt-pill="shipping"]'),
        initialShippingEligible ? RT.config.shippingThreshold : 0,
        'Earned'
      );
      primeSub(
        this.querySelector('[data-rt-pill="gift"]'),
        rtGiftActive() ? RT.config.giftThreshold : 0,
        'In your cart'
      );

      // Set gift image
      if (RT.config.showGiftImage && RT.config.giftImage) {
        const giftIcon = this.querySelector('[data-rt-icon="gift"]');
        if (giftIcon) {
          const img = document.createElement('img');
          img.src = RT.config.giftImage;
          img.alt = '';
          img.width = 24;
          img.height = 24;
          img.loading = 'lazy';
          giftIcon.textContent = '';
          giftIcon.appendChild(img);
        }
      }

      // Geo-check
      RT.shippingEligible = RT.config.shippingCountries.includes(RT.config.country) || RT.config.country === '';

      // Show exactly the pills whose tier can be earned, then collapse the
      // two-column grid when only one survives. Previously the --single class
      // was added in the geo branch only, so a merchant switching the gift
      // tier off got a half-width shipping pill with an empty right column.
      const shippingPill = this.querySelector('[data-rt-pill="shipping"]');
      const giftPill = this.querySelector('[data-rt-pill="gift"]');
      const shippingVisible = rtShippingActive();
      const giftVisible = rtGiftActive();
      if (shippingPill) {
        shippingPill.style.display = shippingVisible ? '' : 'none';
        shippingPill.classList.toggle('reward-thresholds__pill--single', shippingVisible && !giftVisible);
      }
      if (giftPill) {
        giftPill.style.display = giftVisible ? '' : 'none';
        giftPill.classList.toggle('reward-thresholds__pill--single', giftVisible && !shippingVisible);
      }

      // Cache DOM refs
      this.shippingPill = this.querySelector('[data-rt-pill="shipping"]');
      this.giftPill = this.querySelector('[data-rt-pill="gift"]');
      this.shippingStatus = this.querySelector('[data-rt-status="shipping"]');
      this.giftStatus = this.querySelector('[data-rt-status="gift"]');
      // Render sticky bar once globally
      if (!document.querySelector('[data-rt-sticky]') && RT.config.enableSticky) {
        this.renderStickyBar();
      }
      // Inline upsell rendered in Liquid, populated in processCart
      // Mobile checkout bar rendered in Liquid (main-cart.liquid), not JS

      // Show component
      this.removeAttribute('hidden');

      // PWYC preview mode -- inject message above price breakdown in all cart detail containers
      if (RT.config.previewPwyc) {
        document.querySelectorAll('.cart__details').forEach(detailsEl => {
          if (detailsEl.querySelector('.cart__pwyc-message')) return;
          const heartSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 000-7.78z"/></svg>';
          const msg = RT.config.pwycMessage || 'Thank you for being part of our Pay What You Can program';
          const totalRow = detailsEl.querySelector('.cart__subtotal.cart__details--row') || detailsEl.querySelector('.cart__total.cart__details--row');
          if (totalRow) {
            const preview = document.createElement('div');
            preview.className = 'cart__pwyc-message';
            preview.setAttribute('data-rt-pwyc-message', '');
            // Mark as quick-view scope so merchant-authored product
            // links (inline_richtext <a href="/products/...">) open in
            // the modal instead of navigating away. The Liquid-rendered
            // PWYC block in cart-subtotal.liquid carries this attribute
            // too; previewPwyc mode injects a parallel node that must
            // carry it independently or clicks escape to the PDP.
            preview.setAttribute('data-cart-quick-view-scope', '');
            preview.innerHTML = heartSvg + '<span class="cart__pwyc-message-text">' + msg + '</span>';
            totalRow.parentNode.insertBefore(preview, totalRow);
          }
        });
      }

      // Update PWYC message from settings. Uses innerHTML (not textContent)
      // because the setting is typed `inline_richtext` in Shopify's admin,
      // which permits <strong>, <em>, <a>, <br>. Merchant-controlled input
      // only — no user-generated content path reaches this value.
      //
      // The message is wrapped in a single <span class="cart__pwyc-message-text">
      // so that it sits in the flex container as ONE item. Without the wrapper,
      // each inline child (<strong>, <a>, and the text runs between them)
      // becomes its own flex item and picks up the container's gap, which
      // produces the column-per-element layout Seth saw. Inside the span,
      // inline elements flow and wrap normally.
      if (RT.config.pwycMessage) {
        document.querySelectorAll('[data-rt-pwyc-message]').forEach(el => {
          const svg = el.querySelector('svg');
          const svgHtml = svg ? svg.outerHTML : '';
          el.innerHTML = svgHtml + '<span class="cart__pwyc-message-text">' + RT.config.pwycMessage + '</span>';
        });
      }

      // Update empty cart incentive text from settings (hidden in Liquid until JS hydrates with real values)
      const emptyIncentive = document.querySelector('[data-rt-empty-incentive]');
      if (emptyIncentive) {
        const hasShipping = rtShippingActive();
        const hasGift = rtGiftActive();
        if (hasShipping && hasGift) {
          emptyIncentive.textContent = 'Spend ' + rtFormatWhole(RT.config.shippingThreshold / 100) + ' for free shipping, ' + rtFormatWhole(RT.config.giftThreshold / 100) + ' for a free gift!';
          emptyIncentive.removeAttribute('hidden');
        } else if (hasShipping) {
          emptyIncentive.textContent = 'Spend ' + rtFormatWhole(RT.config.shippingThreshold / 100) + ' for free shipping!';
          emptyIncentive.removeAttribute('hidden');
        } else if (hasGift) {
          emptyIncentive.textContent = 'Spend ' + rtFormatWhole(RT.config.giftThreshold / 100) + ' for a free gift!';
          emptyIncentive.removeAttribute('hidden');
        }
      }

      // Bind events and start brand message rotation once globally
      if (!RT.initialized) {
        RT.initialized = true;
        this.bindGlobalEvents();
        this.startBrandMessages();
      }

      // Run recalculate (debounced to prevent rapid-fire from DOM replacement)
      rtScheduleRecalculate();
    }

    bindGlobalEvents() {
      // Listen for cart-updated on AjaxCartForm (Combine dispatches non-bubbling event on this element)
      // Element persists across refreshCart — only innerHTML is replaced, not the element itself
      const cartForm = document.getElementById('AjaxCartForm');
      if (cartForm) {
        cartForm.addEventListener('cart-updated', () => rtScheduleRecalculate());
      }

      // MutationObserver on AjaxCartSubtotal for discount code changes
      // Element persists across refreshCart (innerHTML replaced, element stays)
      const subtotal = document.getElementById('AjaxCartSubtotal');
      if (subtotal) {
        new MutationObserver(() => rtScheduleRecalculate()).observe(subtotal, { childList: true });
      }

      // Free-gift decline ("No thanks") + re-add ("Add free gift").
      // Delegated on document because both controls live in DOM that
      // refreshCart() replaces (cart line + reward pill). Any instance can
      // service the cart-level op, so resolve one live at click time.
      document.addEventListener('click', (e) => {
        const declineBtn = e.target.closest('[data-rt-decline-gift]');
        if (declineBtn) {
          const inst = document.querySelector('reward-thresholds');
          // Only hijack the click if we can service it. With no instance the
          // RT system isn't running (so nothing auto-re-adds anyway) — let the
          // link's native href remove the gift line as a fallback.
          if (inst) {
            e.preventDefault();
            inst.declineGift();
          }
          return;
        }
        const addBtn = e.target.closest('[data-rt-add-gift]');
        if (addBtn) {
          e.preventDefault();
          const inst = document.querySelector('reward-thresholds');
          if (inst) inst.reAddGift();
          return;
        }
      });

      // Sticky bar close
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-rt-sticky-close]')) {
          const sticky = document.querySelector('[data-rt-sticky]');
          if (sticky) {
            sticky.setAttribute('data-enabled', 'false');
            sessionStorage.setItem('rt-sticky-dismissed', 'true');
          }
        }
      });

      // Restore sticky dismissed state
      if (sessionStorage.getItem('rt-sticky-dismissed') === 'true') {
        const sticky = document.querySelector('[data-rt-sticky]');
        if (sticky) sticky.setAttribute('data-enabled', 'false');
      }

      // Mobile sticky checkout bar — show ONLY when the in-page Checkout
      // button is out of view. Use a POSITIVE bottom rootMargin so the
      // observer root is effectively TALLER than the real viewport,
      // which flips isIntersecting to true while the button is still
      // slightly below the visible area. Effect: mobile bar hides
      // BEFORE the main button scrolls into view, eliminating the
      // overlap window where both were visible simultaneously.
      //
      // Prior version used rootMargin '-80px' which does the opposite
      // (shrinks the root, button must rise HIGHER into viewport before
      // intersecting) and caused the exact overlap it claimed to fix.
      // Seth flagged the regression in the live cart on 2026-04-21.
      if (RT.config.enableMobileCheckout) {
        const desktopCheckout = document.getElementById('CheckOut');
        const mobileBar = document.getElementById('MobileCheckoutBar');
        if (desktopCheckout && mobileBar) {
          const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              mobileBar.classList.toggle('cart__mobile-checkout-bar--visible', !entry.isIntersecting);
            });
          }, { threshold: 0, rootMargin: '0px 0px 120px 0px' });
          observer.observe(desktopCheckout);
        }
      }

    }

    processCart(cart) {
      // Find auto-added gift item
      const giftItem = cart.items.find(item =>
        String(item.variant_id) === String(RT.config.giftVariantId) &&
        item.properties && item.properties._free_gift === 'true'
      );

      RT.giftInCart = !!giftItem;
      RT.giftLineKey = giftItem ? giftItem.key : null;
      // The gift is only "free" if the automatic discount actually zeroed it.
      // A conflicting/ineligible discount leaves it chargeable — never tell
      // the customer it's free without checking the price (codex P1).
      RT.giftIsFree = giftItem ? giftItem.final_line_price === 0 : false;

      // Declined state is owned by the cart attribute so it survives reloads
      // and is consistent across page + drawer instances. Mirror it into the
      // in-memory flag for synchronous reads in updateUI().
      RT.giftDeclined = !!(cart.attributes && cart.attributes._gift_declined === 'true');

      // Calculate effective total (exclude auto-added gift)
      const giftLinePrice = giftItem ? giftItem.final_line_price : 0;
      const effectiveTotal = cart.total_price - giftLinePrice;

      // Determine threshold states
      const shippingEarned = rtShippingActive() && effectiveTotal >= RT.config.shippingThreshold;
      const giftEarned = rtGiftActive() && effectiveTotal >= RT.config.giftThreshold;

      // ALWAYS update UI first (never blocked by busy)
      this.updateUI(effectiveTotal, shippingEarned, giftEarned);

      // Update upsell heading based on threshold state (all instances)
      let upsellText;
      if (!shippingEarned && rtShippingActive()) {
        const remaining = Math.ceil((RT.config.shippingThreshold - effectiveTotal) / 100);
        upsellText = 'Add ' + rtFormatWholeNoCents(remaining) + ' more for ' + RT.config.shippingText + '!';
      } else if (!giftEarned && rtGiftActive()) {
        const remaining = Math.ceil((RT.config.giftThreshold - effectiveTotal) / 100);
        const giftLabel = RT.config.giftText + (RT.config.giftValue ? ' (' + RT.config.giftValue + ')' : '');
        upsellText = 'Add ' + rtFormatWholeNoCents(remaining) + ' more for a ' + giftLabel + '!';
      } else {
        // Both thresholds met — use the merchant-editable upsell heading
        // from section settings (falls back to a sensible default).
        upsellText = RT.config.upsellHeading || 'Complete Your Order';
      }
      document.querySelectorAll('[data-rt-upsell-heading]').forEach(el => el.textContent = upsellText);

      // 3-hat tier nudge (snippets/cart-hat-tier-note.liquid): visible at
      // EXACTLY two hat units, so it has to survive a quantity change without a
      // reload. Quantities come from /cart.js, which is authoritative; WHICH
      // lines are hats comes from the rendered cart lines, which carry
      // data-rt-hat="1" from snippets/cart-hat-unit.liquid — the one place that
      // decides what counts, so this recount and the server render cannot
      // drift. Product identity is stable across quantity edits, which is why
      // the DOM is safe to read for it and not for the count itself.
      const hatNotes = document.querySelectorAll('[data-rt-hat-tier-note]');
      if (hatNotes.length) {
        const hatProductIds = new Set(
          Array.from(document.querySelectorAll('[data-rt-hat="1"][data-product-id]'))
            .map(el => String(el.getAttribute('data-product-id')))
        );
        let hatUnits = 0;
        cart.items.forEach(item => {
          if (hatProductIds.has(String(item.product_id))) hatUnits += item.quantity;
        });
        hatNotes.forEach(el => { el.hidden = hatUnits !== 2; });
      }

      // Reload upsell when cart changes (new items added, items removed, or first render)
      const upsellContainers = document.querySelectorAll('[data-rt-inline-upsell-products]');
      if (upsellContainers.length > 0 && cart.item_count > 0 && RT.config.enableUpsell) {
        // The effective total and shipping eligibility stay in this key even
        // though the threshold-close slot was removed (2026-09-14): a
        // quantity-only change can still change a set card's computed savings,
        // and repainting on it is cheap next to showing a stale claim.
        const cartProductIds = cart.items.filter(i => !(i.properties && i.properties._free_gift === 'true')).map(i => i.product_id).sort().join(',')
          + '|' + effectiveTotal + '|' + (rtShippingActive() ? '1' : '0');
        if (cartProductIds !== RT._lastUpsellCartKey) {
          RT._lastUpsellCartKey = cartProductIds;
          this.loadInlineUpsell(cart);
        } else {
          // Still empty (first render) — load anyway
          const anyEmpty = Array.from(upsellContainers).some(c => c.children.length === 0);
          if (anyEmpty) this.loadInlineUpsell(cart);
        }
      }

      // Update mobile checkout bar total and savings
      const mobileBar = document.getElementById('MobileCheckoutBar');
      if (mobileBar) {
        const mobileTotal = mobileBar.querySelector('.cart__mobile-checkout-total-amount');
        if (mobileTotal) mobileTotal.textContent = rtFormatMoney(cart.total_price);
        const mobileSavings = mobileBar.querySelector('.cart__mobile-checkout-savings');
        if (mobileSavings) {
          if (cart.total_discount > 0) {
            mobileSavings.textContent = 'Saving ' + rtFormatMoney(cart.total_discount);
            mobileSavings.style.display = '';
          } else {
            mobileSavings.style.display = 'none';
          }
        }
        if (cart.item_count === 0) mobileBar.classList.remove('cart__mobile-checkout-bar--visible');
      }

      // Reset upsell availability if cart grew (new items added)
      if (cart.item_count > (RT.lastItemCount || 0) && !(shippingEarned && giftEarned)) {
        sessionStorage.removeItem('rt-upsell-shown');
      }
      RT.lastItemCount = cart.item_count;

      // Signature of the cart minus the gift line. A change here means the
      // customer edited the cart, which can free up the inventory that made
      // the last add fail — so a latched sold-out state is retried.
      RT.cartKey = cart.items
        .filter(i => !(i.properties && i.properties._free_gift === 'true'))
        .map(i => i.variant_id + 'x' + i.quantity)
        .sort()
        .join(',');
      if (RT.giftUnavailable && RT.cartKey !== RT.giftUnavailableAtKey) {
        RT.giftUnavailable = false;
      }

      // Update global state
      RT.shippingEarned = shippingEarned;
      RT.giftEarned = giftEarned;

      // Auto-add/remove gift (gated by busy lock to prevent duplicates).
      // Do NOT auto-add when the customer declined the gift — that's the whole
      // point of the "No thanks" control. The decline persists until they
      // re-add it or drop below the threshold.
      if (giftEarned) {
        if (!RT.giftInCart && !RT.giftDeclined && !RT.giftUnavailable && !RT.busy) {
          this.addGift();
        }
      } else {
        if (RT.giftInCart && !RT.busy) {
          this.removeGift();
        }
        // Below threshold (or tier switched off): let a future qualifying
        // cart retry the add rather than latching out of stock forever.
        RT.giftUnavailable = false;
        // Below threshold: clear a stale decline so the next time they
        // qualify, the gift is offered again (decline is per-qualification,
        // not permanent for the cart's life).
        if (RT.giftDeclined && !RT.giftInCart && !RT.busy) {
          this.clearDeclined();
        }
      }
    }

    updateUI(effectiveTotal, shippingEarned, giftEarned) {
      // Shipping pill
      if (this.shippingPill && rtShippingActive()) {
        const shippingSub = this.shippingPill.querySelector('[data-rt-subtitle="shipping"]');
        if (shippingEarned) {
          this.shippingPill.classList.add('reward-thresholds__pill--earned');
          // Visible subtitle: "Earned" (no !) — Variant D calm signal.
          if (shippingSub) shippingSub.textContent = 'Earned';
          // Aria-live announcement keeps the exclamation — screen readers
          // convey excitement through punctuation + prefix.
          if (this.shippingStatus) this.shippingStatus.textContent = 'Free shipping earned!';
          const icon = this.shippingPill.querySelector('[data-rt-icon="shipping"]');
          // stroke-linecap=round matches Variant D's glyph language.
          if (icon) icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5L19 7"/></svg>';
        } else {
          this.shippingPill.classList.remove('reward-thresholds__pill--earned');
          const remaining = Math.ceil((RT.config.shippingThreshold - effectiveTotal) / 100);
          // Visible subtitle uses tiered warm copy from rtProgressCopy().
          // Aria-live status keeps the plain "$X away" string — screen
          // readers shouldn't get the emotive phrasing, just the number.
          if (shippingSub) shippingSub.textContent = rtProgressCopy(remaining);
          if (this.shippingStatus) this.shippingStatus.textContent = rtFormatWholeNoCents(remaining) + ' away';
          const icon = this.shippingPill.querySelector('[data-rt-icon="shipping"]');
          if (icon && !icon.querySelector('img')) {
            icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M5 18h14M5 18l1-10h12l1 10M9 8V5a3 3 0 016 0v3"/></svg>';
          }
        }

        // Shipping pill progress bar
        const shippingBar = this.shippingPill.querySelector('[data-rt-pill-bar="shipping"]');
        if (shippingBar) {
          const progress = Math.min((effectiveTotal / RT.config.shippingThreshold) * 100, 100);
          shippingBar.style.width = progress + '%';
          const shippingBarWrap = this.shippingPill.querySelector('[role="progressbar"]');
          if (shippingBarWrap) shippingBarWrap.setAttribute('aria-valuenow', Math.round(progress));
        }
      }

      // Gift pill
      if (this.giftPill && rtGiftActive()) {
        const giftSub = this.giftPill.querySelector('[data-rt-subtitle="gift"]');
        if (giftEarned && RT.giftDeclined && !RT.giftInCart) {
          // Qualified, but the customer removed the gift via "No thanks".
          // Offer a one-click path back in (errant-click recovery).
          // !giftInCart guard (codex): while a decline's removal is still
          // pending or has failed, the gift is STILL in the cart — showing
          // the re-add button then can duplicate the gift line. In-cart +
          // declined + chargeable falls through to the pricing branch below,
          // which retries the removal instead.
          this.giftPill.classList.add('reward-thresholds__pill--earned');
          if (giftSub) {
            giftSub.textContent = '';
            const reAdd = document.createElement('button');
            reAdd.type = 'button';
            reAdd.className = 'reward-thresholds__readd';
            reAdd.setAttribute('data-rt-add-gift', '');
            reAdd.textContent = 'Add free gift';
            giftSub.appendChild(reAdd);
          }
          if (this.giftStatus) this.giftStatus.textContent = 'Free gift available. Add it back to your cart';
          const icon = this.giftPill.querySelector('[data-rt-icon="gift"]');
          if (icon) {
            if (RT.config.showGiftImage && RT.config.giftImage) {
              icon.innerHTML = '<img src="' + RT.config.giftImage + '" alt="" width="24" height="24" loading="lazy">';
            } else {
              icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="13" rx="1"/><path d="M12 8v13M3 12h18M7.5 8C7.5 5 9 4 12 4s4.5 1 4.5 4"/></svg>';
            }
          }
        } else if (giftEarned && RT.giftInCart && !RT.giftIsFree) {
          // Earned + in cart but the discount did NOT zero the line: the
          // customer would be charged for a "free" gift. Route through the
          // DECLINE machinery (codex review of the first fix): declineGift()
          // removes the line, sets the persistent _gift_declined attribute
          // (blocks the auto-add that would otherwise re-add -> remove in an
          // infinite loop), and the declined branch above renders the manual
          // "Add free gift" recovery button — each retry is a click, never
          // automatic. Its await/refresh flow also means a failed removal
          // keeps the gift visibly in the cart instead of celebrating.
          this.giftPill.classList.remove('reward-thresholds__pill--earned');
          if (giftSub) giftSub.textContent = 'Gift pricing issue, removing…';
          if (this.giftStatus) this.giftStatus.textContent = '';
          if (!RT.busy) this.declineGift();
        } else if (giftEarned && RT.giftUnavailable && !RT.giftInCart) {
          // Qualified, but the gift variant is sold out (DENY inventory), so
          // /cart/add.js refused it. Say so instead of celebrating a gift the
          // customer will never receive.
          this.giftPill.classList.remove('reward-thresholds__pill--earned');
          if (giftSub) giftSub.textContent = 'Sold out';
          if (this.giftStatus) this.giftStatus.textContent = 'The free gift is sold out';
        } else if (giftEarned) {
          this.giftPill.classList.add('reward-thresholds__pill--earned');
          if (giftSub) giftSub.textContent = 'In your cart';
          if (this.giftStatus) this.giftStatus.textContent = 'Free gift added to cart!';
          const icon = this.giftPill.querySelector('[data-rt-icon="gift"]');
          if (icon) icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5L19 7"/></svg>';
        } else {
          this.giftPill.classList.remove('reward-thresholds__pill--earned');
          const remaining = Math.ceil((RT.config.giftThreshold - effectiveTotal) / 100);
          // Same tiered warm copy as shipping pill.
          if (giftSub) giftSub.textContent = rtProgressCopy(remaining);
          if (this.giftStatus) this.giftStatus.textContent = rtFormatWholeNoCents(remaining) + ' away';
          const icon = this.giftPill.querySelector('[data-rt-icon="gift"]');
          if (icon) {
            if (RT.config.showGiftImage && RT.config.giftImage) {
              icon.innerHTML = '<img src="' + RT.config.giftImage + '" alt="" width="24" height="24" loading="lazy">';
            } else {
              icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="13" rx="1"/><path d="M12 8v13M3 12h18M7.5 8C7.5 5 9 4 12 4s4.5 1 4.5 4"/></svg>';
            }
          }
        }

        // Gift pill progress bar
        const giftBar = this.giftPill.querySelector('[data-rt-pill-bar="gift"]');
        if (giftBar) {
          const progress = Math.min((effectiveTotal / RT.config.giftThreshold) * 100, 100);
          giftBar.style.width = progress + '%';
          const giftBarWrap = this.giftPill.querySelector('[role="progressbar"]');
          if (giftBarWrap) giftBarWrap.setAttribute('aria-valuenow', Math.round(progress));
        }
      }

      // Sticky mobile bar
      const stickyBar = document.querySelector('[data-rt-sticky]');
      if (stickyBar && RT.config.enableSticky) {
        const stickyText = stickyBar.querySelector('[data-rt-sticky-text]');
        const stickyBarFill = stickyBar.querySelector('[data-rt-sticky-bar]');

        const stickyNothingLeft =
          (shippingEarned || !rtShippingActive()) &&
          (!rtGiftActive() || (giftEarned && (RT.giftUnavailable || !RT.giftDeclined)));
        if (stickyNothingLeft) {
          stickyBar.setAttribute('data-enabled', 'false');
        } else if (shippingEarned && giftEarned && RT.giftDeclined) {
          // Both thresholds met but the gift was declined: there's no "$X
          // more" progress to show, so repurpose the sticky bar as a
          // persistent recovery path. The inline button reuses the
          // [data-rt-add-gift] delegation (re-adds the gift).
          stickyBar.setAttribute('data-enabled', 'true');
          if (stickyText) stickyText.innerHTML = 'Free gift removed. <button type="button" class="reward-thresholds__readd" data-rt-add-gift>Add it back</button>';
          if (stickyBarFill) stickyBarFill.style.width = '100%';
        } else {
          let nextLabel, nextThreshold;
          if (!shippingEarned && rtShippingActive()) {
            nextThreshold = RT.config.shippingThreshold;
            nextLabel = RT.config.shippingText;
          } else {
            nextThreshold = RT.config.giftThreshold;
            nextLabel = RT.config.giftText;
          }
          const remaining = Math.ceil((nextThreshold - effectiveTotal) / 100);
          if (stickyText) stickyText.innerHTML = 'Add <strong>' + rtFormatWholeNoCents(remaining) + '</strong> more for ' + nextLabel;
          if (stickyBarFill) stickyBarFill.style.width = Math.min((effectiveTotal / nextThreshold) * 100, 100) + '%';
        }
      }

      // All-earned state: inject the celebration banner AND mark the
      // host element so CSS can hide the (now-purposeless) reward pills.
      // Pills exist to motivate reaching a threshold — once both are
      // reached they become chrome. Warby / Our Place / Allbirds all
      // collapse the progress UI once the reward is unlocked.
      // Note: a DECLINED gift is NOT "earned" for collapse purposes — the
      // gift isn't in the cart, and collapsing the pills here would hide the
      // "Add free gift" recovery button rendered above, trapping the customer
      // in the celebration state with no way back. Keep pills visible while
      // declined so recovery stays one click away.
      let allEarnedEl = this.querySelector('[data-rt-all-earned]');
      // Never celebrate over a chargeable gift (codex): a gift line the
      // discount failed to zero must not trigger the "Unlocked!" banner.
      // With the gift tier off, "all earned" means free shipping alone, and
      // the banner uses the shipping-only copy so it never promises a gift
      // that isn't running. A sold-out gift is not earned for this purpose.
      const giftSatisfied = !rtGiftActive() ||
        (giftEarned && !RT.giftDeclined && !RT.giftUnavailable && (!RT.giftInCart || RT.giftIsFree));
      const shippingSatisfied = !rtShippingActive() || shippingEarned;
      const anyTierActive = rtShippingActive() || rtGiftActive();
      if (anyTierActive && shippingSatisfied && giftSatisfied) {
        this.classList.add('reward-thresholds--all-earned');
        if (!allEarnedEl) {
          allEarnedEl = document.createElement('div');
          allEarnedEl.className = 'reward-thresholds__all-earned';
          allEarnedEl.setAttribute('data-rt-all-earned', '');
          allEarnedEl.textContent = rtCelebrationText();
          this.querySelector('.reward-thresholds__pills').insertAdjacentElement('afterend', allEarnedEl);
        }
      } else {
        this.classList.remove('reward-thresholds--all-earned');
        if (allEarnedEl) allEarnedEl.remove();
      }
    }

    async addGift() {
      if (RT.busy) return;
      RT.busy = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: parseInt(RT.config.giftVariantId),
              quantity: 1,
              properties: { _free_gift: 'true' }
            }]
          })
        });

        if (!response.ok) {
          console.warn('RewardThresholds: Could not add gift', response.status);
          // 422 is Shopify's "can't add this line" — on a DENY variant that
          // means sold out, and retrying on every recalculate just fires
          // failing POSTs while the pill claims the gift is in the cart.
          // Latch it, repaint honestly, and let a sub-threshold cart clear
          // the latch. Other statuses stay retryable (transient/network).
          if (response.status === 422) {
            RT.giftUnavailable = true;
            RT.giftUnavailableAtKey = RT.cartKey;
            RT.busy = false;
            rtScheduleRecalculate();
            return;
          }
          RT.busy = false;
          return;
        }

        RT.giftUnavailable = false;

        RT.giftInCart = true;
        // refreshCart() starts async fetch internally — observer MUST be set up after this call
        // but before the fetch resolves. Safe: JS is single-threaded, fetch callback can't fire
        // until current stack clears, so rtWaitForCartRefresh always wins the race.
        if (typeof window.refreshCart === 'function') {
          window.refreshCart();
        }
        rtWaitForCartRefresh(() => {
          RT.busy = false;
          rtScheduleRecalculate();
        });
      } catch (e) {
        console.error('RewardThresholds: Error adding gift', e);
        RT.busy = false;
      }
    }

    async removeGift() {
      if (RT.busy || !RT.giftLineKey) return;
      RT.busy = true;

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: RT.giftLineKey,
            quantity: 0
          })
        });

        if (!response.ok) {
          console.warn('RewardThresholds: Could not remove gift', response.status);
          RT.busy = false;
          return;
        }

        RT.giftInCart = false;
        RT.giftLineKey = null;

        // See addGift comment — same ordering safety applies here
        if (typeof window.refreshCart === 'function') {
          window.refreshCart();
        }
        rtWaitForCartRefresh(() => {
          RT.busy = false;
          rtScheduleRecalculate();
        });
      } catch (e) {
        console.error('RewardThresholds: Error removing gift', e);
        RT.busy = false;
      }
    }

    // Customer clicked "No thanks" on the auto-added gift line. Persist the
    // decline as a cart attribute FIRST (so any recalculate triggered by the
    // line removal already sees declined=true and won't re-add), then remove
    // the gift line. RT.giftDeclined is set synchronously up front to win any
    // race with an in-flight processCart before the attribute write resolves.
    async declineGift() {
      if (RT.busy) return;
      RT.busy = true;

      try {
        // Recover the gift line key if a recalc hasn't populated it yet (or a
        // prior /cart.js fetch failed) so the control never silently no-ops.
        let lineKey = RT.giftLineKey;
        if (!lineKey) {
          const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
          if (cartRes.ok) {
            const cart = await cartRes.json();
            const giftItem = cart.items.find(item =>
              String(item.variant_id) === String(RT.config.giftVariantId) &&
              item.properties && item.properties._free_gift === 'true'
            );
            lineKey = giftItem ? giftItem.key : null;
          }
        }
        if (!lineKey) { RT.busy = false; return; }

        // Set the synchronous mirror only once we're committed to removing,
        // so a bail-out above can't leave a false "declined" state.
        RT.giftDeclined = true;

        const attrRes = await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { _gift_declined: 'true' } })
        });
        if (!attrRes.ok) {
          console.warn('RewardThresholds: Could not set decline attribute', attrRes.status);
          RT.busy = false;
          return;
        }

        const removeRes = await fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: 0 })
        });
        if (!removeRes.ok) {
          console.warn('RewardThresholds: Could not remove declined gift', removeRes.status);
          // The attribute write succeeded but the line removal didn't — the
          // gift is still in the cart. Roll back _gift_declined so the cart
          // and UI stay consistent (otherwise the gift shows as declined
          // while present, and re-add could create a duplicate line).
          RT.giftDeclined = false;
          try {
            await fetch('/cart/update.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ attributes: { _gift_declined: '' } })
            });
          } catch (e) { /* best-effort rollback */ }
          RT.busy = false;
          return;
        }

        RT.giftInCart = false;
        RT.giftLineKey = null;
        if (typeof window.refreshCart === 'function') window.refreshCart();
        rtWaitForCartRefresh(() => {
          RT.busy = false;
          rtScheduleRecalculate();
        });
      } catch (e) {
        console.error('RewardThresholds: Error declining gift', e);
        RT.busy = false;
      }
    }

    // Customer clicked "Add free gift" after previously declining. Clear the
    // decline attribute, then reuse the normal addGift() path.
    async reAddGift() {
      if (RT.busy) return;
      RT.giftDeclined = false;
      RT.busy = true;
      try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { _gift_declined: '' } })
        });
        if (!res.ok) {
          // Clear failed server-side — do NOT add, or the cart attribute stays
          // 'true' and the next recalc re-marks the (now-present) gift as
          // declined, desyncing UI from cart. Revert and bail.
          console.warn('RewardThresholds: Could not clear decline attribute', res.status);
          RT.giftDeclined = true;
          RT.busy = false;
          return;
        }
      } catch (e) {
        console.error('RewardThresholds: Error clearing decline', e);
        RT.giftDeclined = true;
        RT.busy = false;
        return;
      }
      RT.busy = false;
      // addGift() takes its own busy lock and refreshes the cart.
      this.addGift();
    }

    // Reset a stale decline attribute once the cart drops below the gift
    // threshold, so re-qualifying offers the gift again. Fire-and-forget;
    // no cart refresh needed (nothing visible changes here).
    async clearDeclined() {
      if (RT.busy) return;
      RT.busy = true;
      RT.giftDeclined = false;
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { _gift_declined: '' } })
        });
      } catch (e) {
        console.error('RewardThresholds: Error clearing stale decline', e);
      }
      RT.busy = false;
    }

    // --- Dynamic DOM Rendering ---

    renderStickyBar() {
      const sticky = document.createElement('div');
      sticky.className = 'reward-thresholds-sticky';
      sticky.setAttribute('data-rt-sticky', '');
      sticky.setAttribute('data-enabled', 'true');
      // Colors come from --cart-* tokens via the .reward-thresholds-sticky
      // selector in component-reward-thresholds.css. No inline setProperty
      // needed — prior code copied from this.style which is now empty
      // after removing the element-level color overrides.
      sticky.innerHTML = `
        <div class="reward-thresholds-sticky__progress">
          <div class="reward-thresholds-sticky__text" data-rt-sticky-text></div>
          <div class="reward-thresholds-sticky__bar">
            <div class="reward-thresholds-sticky__bar-fill" data-rt-sticky-bar></div>
          </div>
        </div>
        <button class="reward-thresholds-sticky__close" data-rt-sticky-close aria-label="Dismiss">&times;</button>
      `;
      const progressArea = sticky.querySelector('.reward-thresholds-sticky__progress');
      if (progressArea) {
        progressArea.style.cursor = 'pointer';
        progressArea.addEventListener('click', () => {
          const cart = document.getElementById('AjaxCartForm') || document.getElementById('cart');
          if (cart) cart.scrollIntoView({ behavior: 'smooth' });
        });
      }
      document.body.appendChild(sticky);
    }

    // --- Cart upsell rail ---
    //
    // Ranking (2026-09-14 rebuild). The old rail asked
    // /recommendations/products.json?intent=related for the cart's first line
    // and showed whatever came back, which for a hat cart was six more hats,
    // the one thing hat buyers never do (adult hats run 1.16 units/order, zero
    // adult+adult pairs in 90 days). Evidence-ranked candidates now come from
    // Liquid (snippets/cart-rail-meta.liquid) and recommendations only fill the
    // remaining slots:
    //   1 Twinsie Set for a mapped hat      badge "Adult + kids set, save $X"
    //   2 Twinsie pair hat                  no badge (the card title says Kids Hat)
    //   3 same-design other category        no badge (Seth 2026-09-14: shoppers
    //     can see the design match; a label is noise)
    //     (kitchen towel, then mug/drinkware, then sticker, then the rest)
    //   4..6 /recommendations complementary, then related as last resort
    //
    // A design whose pair is COMPLETE in the cart contributes neither a set nor
    // a pair card: Adoric adds a set's component hats, never the set parent, so
    // the parent's product id never matches a cart line and the id de-dupe let
    // the set card through (live defect, 2026-09-14). Suppression is by handle.
    // The threshold-close promotion was removed the same day: the rail heading
    // already states the free-shipping gap, and the rule had no price ceiling,
    // so a $12 gap promoted a $59.50 set.
    //
    // Every add posts _rail / _rail_rank / _rail_reason line properties so
    // attach rate and CM2 are countable in shopify_order_line_items. Underscore
    // properties are hidden from the customer by both cart item snippets.
    parseCartRailMeta() {
      const node = document.querySelector('[data-rt-cart-meta]');
      if (!node) return [];
      try {
        const parsed = JSON.parse(node.textContent || '{}');
        return Array.isArray(parsed.items) ? parsed.items : [];
      } catch (e) {
        return [];
      }
    }

    railBadge(reason, c) {
      if (reason === 'set') {
        // Name what the set IS. The old copy leaned on the product title to
        // carry "adult + kids", which it does not do for a shopper skimming
        // the rail (live defect, 2026-09-14).
        return c.savings > 0
          ? 'Adult + kids set, save ' + rtFormatMoney(c.savings)
          : 'Adult + kids set';
      }
      // Only the set carries a badge: it is the one card whose value (the
      // savings, and what the set is) is not visible from title and price.
      return '';
    }

    async loadInlineUpsell(cart) {
      const containers = document.querySelectorAll('[data-rt-inline-upsell-products]');
      if (containers.length === 0) return;

      // Loads are now total-sensitive, so two can be in flight at once (a
      // quantity change while a recommendations fetch is pending). Without a
      // generation guard the slower, older load wins the render and puts a
      // stale ranking back on screen. Only the newest load may paint.
      RT._upsellGeneration = (RT._upsellGeneration || 0) + 1;
      const generation = RT._upsellGeneration;
      const superseded = () => generation !== RT._upsellGeneration;

      const hideRail = () => document.querySelectorAll('[data-rt-inline-upsell]').forEach(w => w.style.display = 'none');

      const realItems = cart.items.filter(item => !(item.properties && item.properties._free_gift === 'true'));
      if (realItems.length === 0) { hideRail(); return; }
      // Seed the recommendations fill from the FIRST real line. Shopify's cart
      // returns the most recently added line first, so this is the newest
      // intent signal, not the oldest. Unchanged from the pre-rebuild code.
      const seedItem = realItems[0];

      // Preorder detection — exact tag match. Trek Light standard: Preorder-ON.
      const PREORDER_TAG = 'Preorder-ON';
      const isPreorderProduct = (p) => (p.tags || []).includes(PREORDER_TAG);
      // Case-insensitive deny list for products that must never appear in upsells.
      const DENY_TAGS_LOWER = ['discount_hidden_product', 'discontinued',
        // bundle-parent: Adoric bundle parents. Their purchase runs through the
        // widget on their own PDP; a card's quick-view adds the PARENT variant,
        // bypassing component inventory, preorder plans, and the app discount.
        // Twinsie Sets are bundle parents, so their cards carry link_only and
        // route to the set PDP instead of adding anything to the cart here.
        'bundle-parent'];
      // cartHasPreorder: prefer explicit DOM signal from rendered badge; if
      // gate mismatch prevents badge rendering, fall back to tag check against
      // the fetched cart (but cart.js doesn't expose tags, so we rely on DOM).
      const cartHasPreorder = document.querySelector('.cart-item__preorder') !== null;

      // Every cart line counts as "already in cart", gift lines included: if the
      // gift tier uses a non-first variant of a multi-variant product, that
      // product could otherwise come back as a paid card while its gift variant
      // sits in the cart. realItems is only for seeding and totals.
      const inCartIds = new Set(cart.items.map(i => String(i.product_id)));
      const cartTypes = new Set(
        Array.from(document.querySelectorAll('[data-rt-type]'))
          .map(el => String(el.getAttribute('data-rt-type') || '').toLowerCase())
          .filter(Boolean)
      );

      // Family ordering: kitchen towels first (highest cross-category lift and
      // 2.72 units/order), then drinkware, then stickers, then everything else.
      const familyRank = (type) => {
        const t = String(type || '').toLowerCase();
        if (t.indexOf('towel') !== -1 && t.indexOf('travel') === -1) return 0;
        if (t.indexOf('mug') !== -1 || t.indexOf('drinkware') !== -1) return 1;
        if (t.indexOf('sticker') !== -1) return 2;
        // A sibling in a category already in the cart is the weakest of the
        // same-design options, so it sorts behind the untried categories.
        if (cartTypes.has(t)) return 4;
        return 3;
      };

      // A Twinsie Set is a bundle parent by definition, so link_only cards are
      // exempt from THAT tag only. `discontinued` and `discount_hidden_product`
      // still exclude a set: neither the Liquid set branch nor
      // `ml-bundle-buyable` looks at them, so this is the only gate that keeps
      // a retired or hidden set out of the rail.
      const denied = (tags, linkOnly) => {
        const lower = (tags || []).map(t => String(t).toLowerCase());
        return DENY_TAGS_LOWER.some(d => {
          if (linkOnly && d === 'bundle-parent') return false;
          return lower.includes(d);
        });
      };

      const seen = new Set();
      const candidates = [];
      const pushCandidate = (c) => {
        const key = String(c.id);
        if (!c.id || seen.has(key) || inCartIds.has(key)) return;
        if (!(c.price > 0)) return;
        if (String(c.variant_id) === String(RT.config.giftVariantId)) return;
        if (denied(c.tags, c.link_only)) return;
        if (cartHasPreorder ? !c.preorder : !!c.preorder) return;
        seen.add(key);
        candidates.push(c);
      };

      const meta = this.parseCartRailMeta();
      // Handles actually in the cart, from the same Liquid block that produced
      // the candidates, so set/pair suppression compares like with like.
      const cartHandles = new Set(meta.map(line => String(line.handle || '')).filter(Boolean));
      // A design is "complete" when both hats of its Twinsie pair are in the
      // cart. Its set and its pair have nothing left to offer.
      const pairComplete = (line) => {
        const partner = line && line.pair_handle;
        return !!partner && cartHandles.has(String(partner));
      };
      // A set is also dead when every hat it bundles is already in the cart,
      // even if that is only visible through the set's own component list (the
      // two trout sets share the kids hat but differ on the adult color).
      const componentsInCart = (c) => {
        const parts = Array.isArray(c.components) ? c.components.filter(Boolean) : [];
        return parts.length > 0 && parts.every(h => cartHandles.has(String(h)));
      };
      const fromMeta = (reason) => {
        meta.forEach(line => {
          const complete = pairComplete(line);
          (line.candidates || []).forEach(c => {
            if (c.reason !== reason || c.available === false) return;
            if (complete && (reason === 'set' || reason === 'pair')) return;
            if (reason === 'set' && componentsInCart(c)) return;
            pushCandidate({
              id: c.id,
              title: c.title,
              url: c.url,
              price: c.price,
              type: c.type,
              tags: c.tags || [],
              image: c.image || '',
              variant_id: c.variant_id,
              variants_count: c.variants_count || 1,
              preorder: !!c.preorder,
              link_only: !!c.link_only,
              reason: reason,
              badge: this.railBadge(reason, c),
              familyRank: familyRank(c.type)
            });
          });
        });
      };

      fromMeta('set');
      fromMeta('pair');
      const beforeFamily = candidates.length;
      fromMeta('family');
      // Sort the family block only, so sets and pairs keep their slots.
      const family = candidates.splice(beforeFamily).sort((a, b) => a.familyRank - b.familyRank);
      family.forEach(c => candidates.push(c));

      // Fill the remaining slots from Shopify recommendations.
      const fillFromRecs = async (intent) => {
        if (candidates.length >= 6) return;
        const response = await fetch('/recommendations/products.json?product_id=' + seedItem.product_id + '&limit=20&intent=' + intent);
        // A non-200 (throttling, most likely) must reach the catch path. Read
        // as a successful empty list it would leave the retry key in place, and
        // a later recalculation on the same key would skip the reload and leave
        // the rail hidden after the API recovered.
        if (!response.ok) throw new Error('recommendations ' + intent + ' HTTP ' + response.status);
        const data = await response.json();
        (data.products || []).forEach(p => {
          if (candidates.length >= 6) return;
          if (p.available === false) return;
          pushCandidate({
            id: p.id,
            title: p.title,
            url: p.url,
            price: p.price,
            type: p.type,
            tags: p.tags || [],
            image: p.featured_image || '',
            variant_id: p.variants && p.variants[0] ? p.variants[0].id : null,
            variants_count: p.variants ? p.variants.length : 1,
            preorder: isPreorderProduct(p),
            link_only: false,
            reason: 'recs',
            badge: '',
            familyRank: 9
          });
        });
      };

      // Each intent gets its own try: a failed `complementary` request must not
      // skip `related`, which is the fallback the rail relied on before this
      // rebuild and the only source of cards for a product with no Liquid
      // candidates.
      let recsFailed = false;
      for (const intent of ['complementary', 'related']) {
        try {
          await fillFromRecs(intent);
        } catch (e) {
          recsFailed = true;
        }
      }
      if (superseded()) return;
      if (recsFailed && candidates.length === 0) {
        // Clear key so next recalculate retries the fetch
        RT._lastUpsellCartKey = null;
        hideRail();
        return;
      }

      const finalCards = candidates.slice(0, 6);
      if (finalCards.length === 0) { hideRail(); return; }
      document.querySelectorAll('[data-rt-inline-upsell]').forEach(w => w.style.display = '');

      const html = finalCards.map((c, idx) => {
        const rank = idx + 1;
        const hasVariants = (c.variants_count || 1) > 1;
        const addLabel = c.preorder ? 'Preorder' : 'Add';
        const safeTitle = rtEscapeHtml(c.title);
        // Shopify product URLs are already URL-safe (/products/handle), don't encodeURI
        const safeUrl = c.url;
        const imgSrc = c.image ? (c.image.indexOf('?') !== -1 ? c.image + '&width=200' : c.image + '?width=200') : '';
        // Preorder items must NOT one-click add: the bare {id, quantity}
        // payload omits the estimated-ship line property + selling plan the
        // real product form attaches (codex #4). Route them through
        // quick-view, which renders the full form incl. preorder properties.
        // link_only (Twinsie Sets, i.e. bundle parents) goes one step further
        // and links straight to the PDP, where the bundle app owns the add.
        let actionHtml;
        if (c.link_only) {
          actionHtml = '<a href="' + safeUrl + '" class="cart__upsell-card-action">Options</a>';
        } else if (hasVariants || c.preorder) {
          actionHtml = '<quick-view-product class="cart__upsell-quickview"><a href="' + safeUrl + '" class="cart__upsell-card-action" data-id="' + c.id + '">' + (c.preorder ? 'Preorder' : 'Options') + '</a></quick-view-product>';
        } else {
          actionHtml = '<button class="cart__upsell-card-action" data-upsell-add="' + c.variant_id + '" data-upsell-label="' + addLabel + '" data-rail-rank="' + rank + '" data-rail-reason="' + c.reason + '">' + addLabel + '</button>';
        }
        const badgeHtml = c.badge ? '<span class="cart__upsell-card-badge">' + rtEscapeHtml(c.badge) + '</span>' : '';
        return `
            <div class="cart__upsell-card">
              <a href="${safeUrl}" class="cart__upsell-card-image">
                <img src="${imgSrc}" alt="${safeTitle}" loading="lazy" width="80" height="80">
              </a>
              <div class="cart__upsell-card-info">
                ${badgeHtml}
                <a href="${safeUrl}" class="cart__upsell-card-title">${safeTitle}</a>
                <div class="cart__upsell-card-price">${rtFormatMoney(c.price)}</div>
                ${actionHtml}
              </div>
            </div>`;
      }).join('');

      // Populate all upsell containers (page + drawer)
      if (superseded()) return;
      containers.forEach(container => {
        container.innerHTML = html;
        // Bind add-to-cart buttons
        container.querySelectorAll('[data-upsell-add]').forEach(btn => {
          btn.addEventListener('click', async (e) => {
            e.preventDefault();
            const originalLabel = btn.dataset.upsellLabel || 'Add to Cart';
            btn.textContent = '...';
            btn.disabled = true;
            const resp = await fetch('/cart/add.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [{
                id: parseInt(btn.dataset.upsellAdd),
                quantity: 1,
                properties: {
                  _rail: 'cart-upsell',
                  _rail_rank: String(btn.dataset.railRank || ''),
                  _rail_reason: String(btn.dataset.railReason || '')
                }
              }] })
            });
            if (resp.ok) {
              btn.textContent = 'Added ✓';
              // refreshCart replaces innerHTML → MutationObserver triggers rtScheduleRecalculate
              // Don't call rtScheduleRecalculate explicitly here, it races with refreshCart
              if (typeof window.refreshCart === 'function') window.refreshCart();
              setTimeout(() => { btn.textContent = originalLabel; btn.disabled = false; }, 3000);
            } else {
              btn.textContent = 'Error';
              setTimeout(() => { btn.textContent = originalLabel; btn.disabled = false; }, 2000);
            }
          });
        });
      });
    }

    startBrandMessages() {
      const messages = RT.config.brandMessages || [
        'Most orders ship within 1-2 business days',
        'Family-owned in Boulder, CO for over 20 years',
        'Every order plants a tree with One Tree Planted',
      ];
      if (messages.length === 0) return;

      // innerHTML (not textContent) because brand_message_N fields
      // are `inline_richtext` type in the section schema — merchant-
      // authored <strong>/<em>/<a>/<br> markup must round-trip.
      // Shopify's inline_richtext strips everything else server-side
      // (allow-list of bold/italic/link/break), so dangerous tags
      // like <script> and <iframe> can't reach this sink.
      const updateAllMessages = (html) => {
        document.querySelectorAll('[data-rt-brand-message]').forEach(el => el.innerHTML = html);
      };

      updateAllMessages(messages[RT.brandMessageIndex || 0]);

      if (messages.length <= 1) return;

      // Pause rotation while the user is hovering or focusing the
      // banner (WCAG 2.2.2 Pause/Stop/Hide respect), and while the
      // tab is hidden (don't burn CPU on a page nobody is looking at,
      // relevant on FB/IG webview where users may background the tab
      // during checkout hesitation). Re-fires immediately on resume.
      RT.brandMessagePaused = RT.brandMessagePaused || false;
      if (!RT.brandMessagePauseBound) {
        RT.brandMessagePauseBound = true;
        const setPaused = (val) => { RT.brandMessagePaused = val; };
        // Match on the band, not the span, so hovering the outgoing
        // ghost mid-slide also pauses.
        const inBand = (e) => e.target.closest('.cart__brand-message, [data-rt-brand-message]');
        document.addEventListener('mouseover', (e) => {
          if (inBand(e)) setPaused(true);
        });
        document.addEventListener('mouseout', (e) => {
          if (inBand(e)) setPaused(false);
        });
        document.addEventListener('focusin', (e) => {
          if (inBand(e)) setPaused(true);
        });
        document.addEventListener('focusout', (e) => {
          if (inBand(e)) setPaused(false);
        });
      }

      // Swap by sliding, never by fading. An opacity fade puts the text
      // through low-contrast frames that axe flags as color-contrast
      // failures (2026-10-20 full-site run, /cart). Here the text keeps
      // its full color every frame: an aria-hidden ghost carries the old
      // message up and out while the real span slides in from below,
      // both clipped by the band's overflow. Reduced motion, or a band
      // that isn't laid out (closed drawer), swaps instantly.
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const SLIDE_MS = 450;
      const swapMessage = (el, html) => {
        const band = el.closest('.cart__brand-message');
        const distance = band ? band.clientHeight : 0;
        if (reduceMotion.matches || !distance) {
          el.innerHTML = html;
          return;
        }
        // The ghost is a centering wrapper around a plain text span, the
        // same shape as the band itself, so inline rich text (<strong>,
        // <a>, <br>) wraps exactly as it did before the swap.
        const ghost = document.createElement('span');
        ghost.className = 'cart__brand-message-ghost';
        ghost.setAttribute('aria-hidden', 'true');
        const ghostText = document.createElement('span');
        ghostText.className = 'cart__brand-message-text';
        ghostText.innerHTML = el.innerHTML;
        // A merchant link copied into the aria-hidden ghost must not be
        // reachable by Tab while it slides out (axe aria-hidden-focus).
        ghost.inert = true;
        ghostText.querySelectorAll('a, button, [tabindex]').forEach(n => n.setAttribute('tabindex', '-1'));
        ghost.appendChild(ghostText);
        band.appendChild(ghost);

        el.classList.remove('is-sliding');
        el.innerHTML = html;
        el.style.transform = `translateY(${distance}px)`;
        void el.offsetHeight; // commit the start position before animating
        el.classList.add('is-sliding');
        ghost.classList.add('is-sliding');
        el.style.transform = '';
        ghost.style.transform = `translateY(-${distance}px)`;
        setTimeout(() => {
          ghost.remove();
          el.classList.remove('is-sliding');
        }, SLIDE_MS + 100);
      };

      if (RT.brandMessageInterval) clearInterval(RT.brandMessageInterval);
      RT.brandMessageInterval = setInterval(() => {
        if (RT.brandMessagePaused || document.visibilityState === 'hidden') return;
        const els = document.querySelectorAll('[data-rt-brand-message]');
        if (els.length === 0) return;
        RT.brandMessageIndex = ((RT.brandMessageIndex || 0) + 1) % messages.length;
        els.forEach(el => swapMessage(el, messages[RT.brandMessageIndex]));
      }, 5000);
    }

  });
}
